import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { useBreakpoints } from 'utils/hooks/UI';
import ListItem from 'components/ListItem';
import {
  getPaymentsTotal,
  getPayments,
  getBalance,
  getLastPayment,
  getTip,
  getPaymentSelected,
} from 'reducers/Payments';
import {
  setPaymentEditIndex,
  setPaymentDecimalEdit,
  deletePayment,
  setPayment,
  setPaymentSelected,
  setShowPaymentInput,
  fillSelectedPayment,
} from 'actions/Payments';
import { generateCustomPaymentKey } from 'utils/payments';
import { addWarning } from 'actions/Error';
import {
  getCustomPaymentTypes,
  getCustomPaymentConfig,
} from 'reducers/PaymentTypes';

const CustomPayment = ({ type, disabled = false }) => {
  const dispatch = useDispatch();
  const isMobileView = !useBreakpoints().mdplus;
  const total = useSelector(getPaymentsTotal);
  const tip = useSelector(getTip);
  const payments = useSelector(getPayments);
  const balance = useSelector(getBalance);
  const { t } = useTranslation('payment');
  const { lastPayment, lastKey } = useSelector(getLastPayment);
  const { name } = useSelector(getCustomPaymentTypes).find(
    p => p.type === type,
  );
  const selectedPaymentKey = useSelector(getPaymentSelected);
  const selectedPayment = useSelector(getPayments)[selectedPaymentKey];
  const customPaymentConfig = useSelector(getCustomPaymentConfig);

  const addCustomPayment = () => {
    if (selectedPayment?.type === type) {
      return dispatch(fillSelectedPayment());
    }

    (async () => {
      await dispatch(setPaymentEditIndex(0));
      await dispatch(setPaymentDecimalEdit(false));
    })();

    const isReturnPayment = total + tip < 0;
    const fullPayment = isReturnPayment ? balance : -balance;
    if (fullPayment > 0) {
      const keys = Object.keys(payments).filter(key => key.includes(type));

      const emptyKey = keys.find(key => payments[key].amount === '0.00');

      const paymentKey = emptyKey || generateCustomPaymentKey(keys, type);

      (async () => {
        if (lastPayment.amount === '0.00') {
          dispatch(deletePayment({ key: lastKey }));
        }
        if (customPaymentConfig[type]?.askForReferenceNumber) {
          dispatch(setPaymentSelected(`custom-payment-type-${type}`));
          return;
        }
        await dispatch(
          setPayment({
            amount: '0.00',
            key: paymentKey,
            caption: name,
            type,
            paymentIntegration: type === 'CARD' ? 'external' : undefined,
          }),
        );
        await dispatch(setPaymentSelected(paymentKey));
      })();
    } else {
      dispatch(
        addWarning(t('alerts.amountCovered'), {
          selfDismiss: 3000,
        }),
      );
    }
  };

  return isMobileView ? (
    <li className="payment-item card-payment" style={{ cursor: 'pointer' }}>
      <div
        tabIndex={0}
        className="button"
        role="button"
        onClick={() => {
          if (customPaymentConfig[type]?.askForReferenceNumber) {
            dispatch(setPaymentSelected(`custom-payment-type-${type}`));
            return;
          }
          dispatch(setShowPaymentInput(true, type));
        }}
      >
        <span className="title">{name}</span>
      </div>
    </li>
  ) : (
    <ListItem
      disabled={disabled}
      variant="grid_yellow"
      action={addCustomPayment}
    >
      <span>{name}</span>
      {selectedPayment?.type === type && (
        <span className="tip">
          {new Intl.NumberFormat(undefined, { style: 'percent' }).format(1)}
        </span>
      )}
    </ListItem>
  );
};

export default CustomPayment;
